import { useState } from "react";
import { SURVEY_QUESTIONS, compileSurveyToStatement, TONE_QUESTIONS, compileToneToStatement } from "../utils/seed.js";

export { SURVEY_QUESTIONS, compileSurveyToStatement, TONE_QUESTIONS, compileToneToStatement };

// ADMIN-12: Default to TONE_QUESTIONS (streamlined 5-question flow)
export function StatementSurvey({form, onComplete, onCancel, questions = TONE_QUESTIONS, compiler = compileToneToStatement}) {
  const [step, setStep] = useState(0); // 0 = intro, 1–N = questions, N+1 = review
  const [answers, setAnswers] = useState({});
  const [other, setOther] = useState({}); // free-text for "Other" options

  const totalQ = questions.length;
  const isIntro = step === 0;
  const isReview = step === totalQ + 1;
  const q = !isIntro && !isReview ? questions[step - 1] : null;
  const progress = isIntro ? 0 : isReview ? 100 : Math.round((step / totalQ) * 100);

  const getAnswer = (id) => answers[id] ?? (questions.find(x=>x.id===id)?.type==="multi" ? [] : "");

  const toggleMulti = (id, val, max) => {
    const cur = getAnswer(id);
    if (cur.includes(val)) {
      setAnswers(a => ({...a, [id]: cur.filter(v=>v!==val)}));
    } else if (cur.length < max) {
      setAnswers(a => ({...a, [id]: [...cur, val]}));
    }
  };

  const setSingle = (id, val) => setAnswers(a => ({...a, [id]: val}));
  const setTextAnswer = (id, val) => setAnswers(a => ({...a, [id]: val}));

  const canProceed = () => {
    if (isIntro) return true;
    if (!q) return true;
    if (q.type === "multi") return getAnswer(q.id).length > 0;
    const val = getAnswer(q.id);
    if (!val) return false;
    if (val === "Other" && !other[q.id]?.trim()) return false;
    return true;
  };

  const finalAnswer = (id) => {
    const raw = getAnswer(id);
    if (raw === "Other") return other[id] || "Other";
    if (Array.isArray(raw)) return raw.map(v => v === "Other" ? (other[id] || "Other") : v);
    return raw;
  };

  const finish = () => {
    const compiled = {};
    questions.forEach(sq => { compiled[sq.id] = finalAnswer(sq.id); });
    const statement = compiler(form, compiled);
    onComplete(statement);
  };

  const accent = "#3b82f6";
  const BtnPill = ({children, active, onClick, disabled}) => (
    <button onClick={onClick} disabled={disabled}
      style={{padding:"10px 14px",borderRadius:10,border:`1.5px solid ${active?accent:"#1e2d45"}`,
        background:active?accent+"22":"#0d1724",color:active?accent:"#94a3b8",fontSize:13,
        cursor:disabled?"default":"pointer",fontFamily:"inherit",fontWeight:active?700:400,
        textAlign:"left",lineHeight:1.45,transition:"all .15s",opacity:disabled?0.5:1}}>
      {children}
    </button>
  );

  return (
    <div style={{maxWidth:560,margin:"0 auto",padding:"0 0 60px",animation:"fadeUp .3s ease"}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
        <div style={{fontSize:12,color:"#475569",fontWeight:600}}>
          {isIntro ? "Tone & Style Builder" : isReview ? "Review & Confirm" : `Question ${step} of ${totalQ}`}
        </div>
        <button onClick={onCancel} style={{background:"none",border:"none",color:"#334155",cursor:"pointer",fontSize:12,fontFamily:"inherit"}}>
          Switch to manual →
        </button>
      </div>

      {/* Progress bar */}
      {!isIntro && (
        <div style={{height:3,background:"#1e2d45",borderRadius:2,marginBottom:28,overflow:"hidden"}}>
          <div style={{height:"100%",width:`${progress}%`,background:`linear-gradient(90deg,${accent},#8b5cf6)`,transition:"width .3s ease",borderRadius:2}}/>
        </div>
      )}

      {/* ── Intro screen ── */}
      {isIntro && (
        <div style={{textAlign:"center",padding:"20px 0 10px"}}>
          <div style={{fontSize:38,marginBottom:16}}>🧠</div>
          <h2 style={{color:"#f1f5f9",fontSize:20,fontWeight:900,fontFamily:"Georgia,serif",margin:"0 0 10px"}}>Tone & Style Builder</h2>
          <p style={{color:"#64748b",fontSize:13,lineHeight:1.8,maxWidth:400,margin:"0 auto 28px"}}>
            Answer {totalQ} quick questions to define how <strong style={{color:"#94a3b8"}}>{form.name||"this candidate"}</strong> communicates. This shapes the AI's tone, not its policies.
          </p>
          <div style={{display:"flex",flexDirection:"column",gap:9,textAlign:"left",marginBottom:32,background:"#0d1724",border:"1px solid #1e2d45",borderRadius:12,padding:16}}>
            {[["🗣️","Communication style"],["🛡️","Handling tough questions"],["💎","Core values"],["💬","Signature phrases"],["🎯","Legacy & vision"]].map(([ic,label])=>(
              <div key={label} style={{display:"flex",alignItems:"center",gap:10,fontSize:13,color:"#94a3b8"}}>
                <span style={{fontSize:16}}>{ic}</span>{label}
              </div>
            ))}
          </div>
          <button onClick={()=>setStep(1)} style={{width:"100%",padding:"14px",background:`linear-gradient(135deg,${accent},#1d4ed8)`,color:"#fff",border:"none",borderRadius:11,fontSize:15,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
            Start Tone Builder →
          </button>
        </div>
      )}

      {/* ── Question screen ── */}
      {q && (
        <div key={q.id} style={{animation:"fadeUp .2s ease"}}>
          <div style={{marginBottom:22}}>
            <h3 style={{color:"#f1f5f9",fontSize:17,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 7px",lineHeight:1.35}}>{q.q}</h3>
            {q.hint && <p style={{color:"#475569",fontSize:12,margin:0,lineHeight:1.6}}>{q.hint}</p>}
            {q.type==="multi" && <p style={{color:"#3b82f6",fontSize:11,margin:"5px 0 0",fontWeight:600}}>Select up to {q.max}</p>}
          </div>

          {/* Single-select pills */}
          {q.type==="single" && (
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
              {q.options.map(opt=>{
                const active = getAnswer(q.id)===opt;
                return (
                  <BtnPill key={opt} active={active} onClick={()=>setSingle(q.id,opt)}>
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      <span style={{width:16,height:16,borderRadius:"50%",border:`2px solid ${active?accent:"#334155"}`,background:active?accent:"transparent",flexShrink:0,display:"inline-block"}}/>
                      {opt}
                    </div>
                  </BtnPill>
                );
              })}
              {getAnswer(q.id)==="Other" && (
                <input
                  autoFocus
                  style={{width:"100%",padding:"11px 13px",background:"#0f1623",border:`1px solid ${accent}`,borderRadius:9,color:"#e2e8f0",fontSize:14,fontFamily:"inherit",outline:"none",marginTop:4}}
                  placeholder="Describe their ideology / style..."
                  value={other[q.id]||""}
                  onChange={e=>setOther(o=>({...o,[q.id]:e.target.value}))}
                />
              )}
            </div>
          )}

          {/* Multi-select pills */}
          {q.type==="multi" && (
            <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16}}>
              {q.options.map(opt=>{
                const sel = getAnswer(q.id).includes(opt);
                const maxed = !sel && getAnswer(q.id).length >= q.max;
                return (
                  <BtnPill key={opt} active={sel} disabled={maxed} onClick={()=>!maxed&&toggleMulti(q.id,opt,q.max)}>
                    <div style={{display:"flex",alignItems:"center",gap:9}}>
                      <span style={{width:16,height:16,borderRadius:4,border:`2px solid ${sel?accent:"#334155"}`,background:sel?accent:"transparent",flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",fontSize:10,color:"#fff"}}>
                        {sel&&"✓"}
                      </span>
                      {opt}
                    </div>
                  </BtnPill>
                );
              })}
            </div>
          )}

          {/* Free text */}
          {q.type==="text" && (
            <textarea
              autoFocus
              style={{width:"100%",padding:"13px 14px",background:"#0d1724",border:`1px solid #1e2d45`,borderRadius:11,color:"#e2e8f0",fontSize:14,fontFamily:"inherit",outline:"none",minHeight:100,lineHeight:1.7,resize:"vertical",marginBottom:4}}
              placeholder={q.placeholder}
              value={getAnswer(q.id)}
              onChange={e=>setTextAnswer(q.id,e.target.value)}
            />
          )}

          {/* Nav */}
          <div style={{display:"flex",gap:9,marginTop:8}}>
            <button onClick={()=>setStep(s=>s-1)} style={{flex:1,padding:"12px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#64748b",fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>← Back</button>
            <button onClick={()=>setStep(s=>s+1)} disabled={!canProceed()}
              style={{flex:2,padding:"12px",background:canProceed()?`linear-gradient(135deg,${accent},#1d4ed8)`:"#1e2d45",color:canProceed()?"#fff":"#475569",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:canProceed()?"pointer":"not-allowed",fontFamily:"inherit"}}>
              {step===totalQ?"Review answers →":"Next →"}
            </button>
          </div>
        </div>
      )}

      {/* ── Review screen ── */}
      {isReview && (
        <div style={{animation:"fadeUp .2s ease"}}>
          <h3 style={{color:"#f1f5f9",fontSize:17,fontWeight:800,fontFamily:"Georgia,serif",margin:"0 0 4px"}}>Review your answers</h3>
          <p style={{color:"#475569",fontSize:12,margin:"0 0 20px"}}>We'll compile these into a rich AI persona statement.</p>
          <div style={{display:"flex",flexDirection:"column",gap:9,marginBottom:24}}>
            {questions.map((sq,i)=>{
              const ans = finalAnswer(sq.id);
              if (!ans || (Array.isArray(ans)&&ans.length===0)) return null;
              return (
                <div key={sq.id} style={{background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,padding:"12px 14px"}}>
                  <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.08em",color:"#475569",textTransform:"uppercase",marginBottom:5}}>Q{i+1} · {sq.q.slice(0,45)}…</div>
                  <div style={{fontSize:13,color:"#94a3b8",lineHeight:1.5}}>
                    {Array.isArray(ans) ? ans.join(", ") : ans}
                  </div>
                  <button onClick={()=>setStep(i+1)} style={{marginTop:7,background:"none",border:"none",color:"#3b82f6",fontSize:11,cursor:"pointer",fontFamily:"inherit",padding:0}}>Edit ✏️</button>
                </div>
              );
            })}
          </div>
          <div style={{display:"flex",gap:9}}>
            <button onClick={()=>setStep(totalQ)} style={{flex:1,padding:"12px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:10,color:"#64748b",fontSize:14,cursor:"pointer",fontFamily:"inherit"}}>← Back</button>
            <button onClick={finish} style={{flex:2,padding:"13px",background:"linear-gradient(135deg,#16a34a,#15803d)",color:"#fff",border:"none",borderRadius:10,fontSize:14,fontWeight:700,cursor:"pointer",fontFamily:"inherit"}}>
              ✦ Build Statement
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StatementSurvey;
